import SportIcon from '@/components/SportIcon';
/**
 * ScorersPage.jsx
 * Admin page: create scorers, assign sports per scorer
 */
import { useEffect, useState, useRef } from 'react';
import { Users, Plus, Edit2, Trash2, Target, Search, Check, X } from 'lucide-react';
import { Card, CardContent } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Badge } from '@/components/ui/badge';
import { Label } from '@/components/ui/label';
import { Skeleton } from '@/components/ui/skeleton';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter } from '@/components/ui/dialog';
import api from '@/lib/api';
import useAuthStore from '@/stores/authStore';
import { toast } from 'sonner';

const SPORT_ICONS = {
  table_tennis:'🏓', badminton:'🏸', chess:'♟️',
  carrom:'🎯', pickleball:'🥒', squash:'🎾',
  cricket:'🏏', football:'⚽',
};

export default function ScorersPage() {
  const { activeOrgSlug, user } = useAuthStore();
  const orgSlug = user?.memberships?.[0]?.slug || activeOrgSlug || 'btcc';
  const token = () => localStorage.getItem('btccp_access_token');

  const [scorers, setScorers]   = useState([]);
  const [sports, setSports]     = useState([]);
  const [loading, setLoading]   = useState(true);

  // Invite new scorer
  const [inviteOpen, setInviteOpen] = useState(false);
  const [inviteForm, setInviteForm] = useState({ phone: '', name: '', sports: [] });
  const [inviting, setInviting]     = useState(false);

  // Edit scorer's sports
  const [editTarget, setEditTarget]   = useState(null);
  const [editSports, setEditSports]   = useState([]);
  const [saving, setSaving]           = useState(false);

  useEffect(() => {
    fetchScorers();
    fetchSports();
  }, [orgSlug]);

  const fetchScorers = async () => {
    setLoading(true);
    try {
      const res = await api.get(`/orgs/${orgSlug}/users`);
      const all = Array.isArray(res.data) ? res.data : [];
      setScorers(all.filter(u => u.roles?.includes('scorer')));
    } catch { toast.error('Failed to load scorers'); }
    finally { setLoading(false); }
  };

  const fetchSports = async () => {
    try {
      // Fetch sport formats from active tournaments (not the global org sports list)
      const tournsRes = await api.get(`/orgs/${orgSlug}/tournaments`);
      const tourns = Array.isArray(tournsRes.data) ? tournsRes.data : [];
      // Use the first active/upcoming tournament, or all tournaments
      const activeTournaments = tourns.filter(t => t.status !== 'cancelled') || tourns;
      const sportSet = new Map(); // code → {code, name}
      for (const t of activeTournaments.slice(0, 5)) {
        try {
          const fmtRes = await api.get(`/orgs/${orgSlug}/tournaments/${t.id}/sport-formats`);
          const fmts = Array.isArray(fmtRes.data) ? fmtRes.data : [];
          fmts.forEach(f => sportSet.set(f.sport_code, { code: f.sport_code, name: f.sport_name || f.sport_code }));
        } catch (_) {}
      }
      setSports(Array.from(sportSet.values()));
    } catch (_) {
      console.warn('Failed to load tournament sports');
    }
  };

  const handleInvite = async () => {
    if (!inviteForm.phone.trim()) { toast.error('Phone is required'); return; }
    setInviting(true);
    try {
      // 1. Invite user with scorer role
      const res = await fetch(`/api/orgs/${orgSlug}/users`, {
        method: 'POST',
        headers: { 'Authorization': `Bearer ${token()}`, 'Content-Type': 'application/json' },
        body: JSON.stringify({
          phone: inviteForm.phone.trim(),
          display_name: inviteForm.name.trim() || undefined,
          roles: ['scorer'],
        }),
      });
      const data = await res.json();
      if (!res.ok) { toast.error(data.detail || 'Failed to invite'); return; }

      // 2. Assign sports if any selected
      if (inviteForm.sports.length > 0) {
        await fetch(`/api/orgs/${orgSlug}/users/${data.id}/scorer-sports`, {
          method: 'PATCH',
          headers: { 'Authorization': `Bearer ${token()}`, 'Content-Type': 'application/json' },
          body: JSON.stringify({ sports: inviteForm.sports }),
        });
      }
      toast.success(`Scorer ${data.display_name || inviteForm.phone} created`);
      setInviteOpen(false);
      setInviteForm({ phone: '', name: '', sports: [] });
      fetchScorers();
    } catch (_) { toast.error('Failed to create scorer'); }
    finally { setInviting(false); }
  };

  const openEditSports = (scorer) => {
    setEditTarget(scorer);
    setEditSports(scorer.scorer_sports || []);
  };

  const handleSaveSports = async () => {
    if (!editTarget) return;
    setSaving(true);
    try {
      await fetch(`/api/orgs/${orgSlug}/users/${editTarget.id}/scorer-sports`, {
        method: 'PATCH',
        headers: { 'Authorization': `Bearer ${token()}`, 'Content-Type': 'application/json' },
        body: JSON.stringify({ sports: editSports }),
      });
      toast.success('Sports assignment saved');
      setEditTarget(null);
      fetchScorers();
    } catch (_) { toast.error('Failed to save'); }
    finally { setSaving(false); }
  };

  const toggleInviteSport = (code) =>
    setInviteForm(f => ({ ...f, sports: f.sports.includes(code) ? f.sports.filter(s=>s!==code) : [...f.sports, code] }));

  const toggleEditSport = (code) =>
    setEditSports(prev => prev.includes(code) ? prev.filter(s=>s!==code) : [...prev, code]);

  return (
    <div className="p-4 sm:p-6 lg:p-8" data-testid="scorers-page">
      {/* Header */}
      <div className="flex items-center justify-between mb-6">
        <div>
          <h1 className="text-2xl sm:text-3xl font-bold text-[#f0f2f7] mb-1">Scorers</h1>
          <p className="text-[#8a93a3]">Manage scorer accounts and their sport assignments</p>
        </div>
        <Button onClick={() => setInviteOpen(true)} className="bg-[#f5c518] text-[#0b0f18] hover:bg-[#f7d24a]">
          <Plus className="h-4 w-4 mr-2"/>Add Scorer
        </Button>
      </div>

      {/* Scorers list */}
      {loading ? (
        <div className="space-y-3">{[1,2,3].map(i=><Skeleton key={i} className="h-20 bg-[#1d2230]"/>)}</div>
      ) : scorers.length === 0 ? (
        <Card className="bg-[#161a26] border-[#252a37]">
          <CardContent className="p-12 text-center">
            <Target className="h-12 w-12 text-[#8a93a3] mx-auto mb-4"/>
            <h3 className="text-lg font-semibold text-[#f0f2f7] mb-2">No scorers yet</h3>
            <p className="text-[#8a93a3] mb-4">Add scorers and assign them sports to manage</p>
            <Button onClick={() => setInviteOpen(true)} className="bg-[#f5c518] text-[#0b0f18]">
              <Plus className="h-4 w-4 mr-2"/>Add First Scorer
            </Button>
          </CardContent>
        </Card>
      ) : (
        <div className="space-y-3">
          {scorers.map(scorer => {
            const assignedSports = scorer.scorer_sports || [];
            return (
              <Card key={scorer.id} className="bg-[#161a26] border-[#252a37] hover:border-[rgba(245,197,24,0.2)] transition-colors">
                <CardContent className="p-4 flex items-center gap-4">
                  {/* Avatar */}
                  <div className="h-11 w-11 rounded-full bg-[#1d2230] flex items-center justify-center font-bold text-[#f5c518] text-base flex-shrink-0">
                    {(scorer.display_name || scorer.phone)?.[0]?.toUpperCase() || 'S'}
                  </div>
                  {/* Info */}
                  <div className="flex-1 min-w-0">
                    <p className="font-semibold text-[#f0f2f7]">{scorer.display_name || scorer.phone}</p>
                    <p className="text-xs text-[#8a93a3] font-mono">{scorer.phone}</p>
                    {/* Assigned sports */}
                    <div className="flex flex-wrap gap-1.5 mt-1.5">
                      {assignedSports.length > 0 ? assignedSports.map(sc => (
                        <span key={sc} className="inline-flex items-center gap-1 text-[10px] bg-[#1d2230] border border-[#252a37] text-[#f0f2f7] px-1.5 py-0.5 rounded">
                          <SportIcon sport={sc} size={16}/>
                          <span className="capitalize">{sc.replace(/_/g,' ')}</span>
                        </span>
                      )) : (
                        <span className="text-[10px] text-[#8a93a3] italic">All sports (no restriction)</span>
                      )}
                    </div>
                  </div>
                  {/* Edit button */}
                  <Button variant="outline" size="sm" onClick={() => openEditSports(scorer)}
                    className="border-[#252a37] text-[#f0f2f7] hover:bg-[#1d2230] flex-shrink-0"
                    data-testid={`edit-scorer-sports-${scorer.id}`}>
                    <Edit2 className="h-3.5 w-3.5 mr-1.5"/>Sports
                  </Button>
                  {/* Delete scorer */}
                  <Button variant="outline" size="sm"
                    onClick={async () => {
                      if (!confirm(`Remove ${scorer.display_name || scorer.phone} as a scorer?`)) return;
                      try {
                        const res = await fetch(`/api/orgs/${orgSlug}/users/${scorer.id}/scorer`, {
                          method: 'DELETE',
                          headers: { Authorization: `Bearer ${token()}` },
                        });
                        if (res.ok) { toast.success('Scorer removed'); fetchScorers(); }
                        else { const d = await res.json(); toast.error(d.detail || 'Failed'); }
                      } catch { toast.error('Failed to remove scorer'); }
                    }}
                    className="border-[rgba(239,68,68,0.3)] text-[#ef4444] hover:bg-[rgba(239,68,68,0.08)] flex-shrink-0"
                    data-testid={`delete-scorer-${scorer.id}`}>
                    <Trash2 className="h-3.5 w-3.5"/>
                  </Button>
                </CardContent>
              </Card>
            );
          })}
        </div>
      )}

      {/* Invite dialog */}
      <Dialog open={inviteOpen} onOpenChange={o => { setInviteOpen(o); if (!o) setInviteForm({ phone:'', name:'', sports:[] }); }}>
        <DialogContent className="bg-[#161a26] border-[#252a37] max-w-md" data-testid="invite-scorer-dialog">
          <DialogHeader>
            <DialogTitle className="text-[#f0f2f7] flex items-center gap-2">
              <Target className="h-5 w-5 text-[#f5c518]"/>Add New Scorer
            </DialogTitle>
          </DialogHeader>
          <div className="space-y-4">
            <div>
              <Label className="text-[#8a93a3]">Phone Number *</Label>
              <Input value={inviteForm.phone} onChange={e => setInviteForm({...inviteForm, phone: e.target.value})}
                placeholder="+91 98765 43210"
                className="bg-[#1d2230] border-[#252a37] text-[#f0f2f7] mt-1"
                data-testid="scorer-phone-input"/>
            </div>
            <div>
              <Label className="text-[#8a93a3]">Name (optional)</Label>
              <Input value={inviteForm.name} onChange={e => setInviteForm({...inviteForm, name: e.target.value})}
                placeholder="e.g., Scorer 1"
                className="bg-[#1d2230] border-[#252a37] text-[#f0f2f7] mt-1"/>
            </div>
            <div>
              <Label className="text-[#8a93a3] mb-2 block">
                Assigned Sports <span className="text-[#8a93a3] font-normal">(leave empty = all sports)</span>
              </Label>
              <div className="flex flex-wrap gap-2">
                {sports.map(sp => {
                  const sel = inviteForm.sports.includes(sp.code);
                  return (
                    <button key={sp.code} type="button" onClick={() => toggleInviteSport(sp.code)}
                      className={`flex items-center gap-1.5 px-3 py-1.5 rounded-lg text-xs font-medium border transition-colors ${
                        sel ? 'bg-[rgba(245,197,24,0.12)] border-[rgba(245,197,24,0.4)] text-[#f5c518]'
                            : 'bg-[#1d2230] border-[#252a37] text-[#8a93a3] hover:border-[#8a93a3]'
                      }`}>
                      <SportIcon sport={sp.code} size={16}/>{sp.name}
                      {sel && <Check className="h-3 w-3 ml-0.5"/>}
                    </button>
                  );
                })}
              </div>
            </div>
            <div className="bg-[#1d2230] rounded-lg p-3 text-xs text-[#8a93a3]">
              <p>Default password: <code className="text-[#f5c518]">CIRCLES</code> · scorer can change after first login</p>
            </div>
          </div>
          <DialogFooter>
            <Button variant="outline" onClick={() => setInviteOpen(false)} className="border-[#252a37] text-[#f0f2f7]">Cancel</Button>
            <Button onClick={handleInvite} disabled={inviting} className="bg-[#f5c518] text-[#0b0f18] hover:bg-[#f7d24a]"
              data-testid="invite-scorer-submit">
              {inviting ? 'Creating...' : 'Create Scorer'}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Edit sports dialog */}
      <Dialog open={!!editTarget} onOpenChange={o => { if (!o) setEditTarget(null); }}>
        <DialogContent className="bg-[#161a26] border-[#252a37] max-w-sm">
          <DialogHeader>
            <DialogTitle className="text-[#f0f2f7]">
              Sports for {editTarget?.display_name || editTarget?.phone}
            </DialogTitle>
          </DialogHeader>
          <div className="space-y-4">
            <p className="text-xs text-[#8a93a3]">Select which sports this scorer can manage. Leave all unselected to allow all sports.</p>
            <div className="flex flex-wrap gap-2">
              {sports.map(sp => {
                const sel = editSports.includes(sp.code);
                return (
                  <button key={sp.code} type="button" onClick={() => toggleEditSport(sp.code)}
                    className={`flex items-center gap-1.5 px-3 py-1.5 rounded-lg text-xs font-medium border transition-colors ${
                      sel ? 'bg-[rgba(245,197,24,0.12)] border-[rgba(245,197,24,0.4)] text-[#f5c518]'
                          : 'bg-[#1d2230] border-[#252a37] text-[#8a93a3] hover:border-[#8a93a3]'
                    }`}>
                    <SportIcon sport={sp.code} size={16}/>{sp.name}
                    {sel && <Check className="h-3 w-3 ml-0.5"/>}
                  </button>
                );
              })}
            </div>
          </div>
          <DialogFooter>
            <Button variant="outline" onClick={() => setEditTarget(null)} className="border-[#252a37] text-[#f0f2f7]">Cancel</Button>
            <Button onClick={handleSaveSports} disabled={saving} className="bg-[#f5c518] text-[#0b0f18]">
              {saving ? 'Saving...' : 'Save Assignment'}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
}
